import { ObjectMap } from '@react-three/fiber';
import { ExtendedGenerationRecord } from 'dynamo-daos';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useSnapshot } from 'valtio';

// #region GENERIC TYPES
export type FromStore<T extends object> = ReturnType<typeof useSnapshot<T>>;

export type AsyncState<T, E = string> =
  | { status: 'initial'; data: undefined }
  | { status: 'pending'; data?: T }
  | { status: 'error'; data: undefined; error: E }
  /** `data` may be undefined when the request succeeded but there is no resource (e.g. no API key yet). */
  | { status: 'ready'; data: T | undefined };

export type PaginatedData<T> = {
  items: T;
  nextToken?: string;
};

export type PaginatedAsyncState<T, E = string> = AsyncState<
  PaginatedData<T>,
  E
>;
// #endregion

// #region DOMAIN SPECIFIC TYPES
export interface TagInfo {
  tag: string;
  doc_count: number;
}

export interface SubcategoryInfo {
  subcategory: string;
  doc_count: number;
  tags: TagInfo[];
}

export interface CategoryAggregation {
  category: string;
  subcategories: SubcategoryInfo[];
  doc_count: number;
}

export type BrowseSearchQueryParams = {
  category?: string;
  pageSize: number;
  searchAfter?: [number, string]; // pagination token for searching subsequent pages
  searchTerm?: string;
  subcategory?: string;
  tags?: string[]; // optional
  review?: boolean; // Added for review mode
};

export type BrowseResultItem = {
  categoryAndSubcategories: readonly string[];
  gifPathV2: string;
  jobID: string;
  mp4Path: string;
  mp4PathV2: string;
  short: string;
  tags: readonly string[];
  title: string;
};

export type BrowseSearchResults = {
  items: BrowseResultItem[];
  categoryInformation: CategoryAggregation[];
  total: number | undefined;
  pageSize: number;
  hasMore: boolean;
  lastSort: [number, string] | undefined;
};

/** Organization billing/API tier from the user-api organization record. */
export type OrganizationPlanType =
  | 'api_standard'
  | 'custom'
  | 'individual_basic'
  | 'individual_pro'
  | 'team_standard'
  | 'team_pro'
  | 'team_scale'
  | 'platform'
  | 'legacy_free'
  | 'legacy_pro'
  | 'legacy_unlimited';

export type OrganizationInfo = {
  accessLevel?: 'EDIT' | 'VIEW' | 'INVITED_EDIT' | 'INVITED_VIEW';
  /** When true, caller is an org admin without a paid seat assignment. */
  isOrgAdminOnly?: boolean;
  // Always present
  createdAt: string;
  createdBy: string;
  organizationName: string;
  organizationID: string;
  // Billing / plan (from organization record)
  planType?: OrganizationPlanType;
  activeAPIKeys?: number;
  activeSeats?: number;
  /** Sum of current credit balance and purchased credits (when set by API). */
  availableCredits?: number;
  allowedNumAPIKeys?: number;
  allowedNumBatches?: number;
  allowedNumBatchSize?: number;
  billingCycle?: 'monthly' | 'yearly';
  billingCycleStartDate?: string;
  costPerCredit?: number;
  currentCreditBalance?: number;
  inStripeFreeTrialPeriod?: boolean;
  monthlyCreditRefreshAmount?: number;
  nextAutomatedCreditRefreshDate?: string;
  nextBillingDate?: string;
  purchasedCredits?: number;
  seatsPurchased?: number;
  topUpAmountUSD?: number;
  // Present only when caller can edit
  activeProfessionalSeats?: number;
  activeUnlimitedSeats?: number;
  stripeCustomerID?: string;
  totalProfessionalSeats?: number;
  totalUnlimitedSeats?: number;
};

export type OrganizationServiceAccountSummary = {
  serviceID: string;
  serviceName: string;
  accountBalanceUSD: number;
  apiKeyLast4Digits: string;
  createdAt: string;
  createdBy: string;
  invalidAfter: string;
  accessLevel: string;
  allowedOverdraftUSD: number;
  numParallelJobs: number;
  organizationID: string;
  topUpAmountUSD: number;
};

export type OrganizationMember = {
  userID: string;
  accessLevel: 'EDIT' | 'VIEW' | 'INVITED_EDIT' | 'INVITED_VIEW';
  firstName: string;
  lastActiveAt: string;
  lastName: string;
  preferredName: string;
  planType: 'free' | 'pro' | 'unlimited';
  email: string;
  /** Admin access without occupying a purchased seat (excluded from seat roster). */
  isOrgAdminOnly?: boolean;
};

/** Pending team email invite returned by GET /organizations/{id}/invites. */
export type OrganizationPendingInvite = {
  inviteID: string;
  createdAt: string;
  createdBy: string;
  createdForEmail: string;
  inviteExpiresAt: string;
  isOrgAdminOnly: boolean;
  requestedAccessLevel: 'EDIT' | 'VIEW';
};

export type OrganizationDetails = {
  organizationInfo: OrganizationInfo;
  members: OrganizationMember[];
  serviceAccounts: OrganizationServiceAccountSummary[];
};

export type OrganizationMemberSeatType = 'team' | 'pro' | 'unlimited';

/** Team workspace invites only; server derives accessLevel and seat usage. */
export type TeamWorkspaceInviteRole = 'member' | 'admin_only' | 'admin_member';

export type AddOrganizationMemberBody = {
  seatType: OrganizationMemberSeatType;
  accessLevel?: 'EDIT' | 'VIEW';
  workspaceRole?: TeamWorkspaceInviteRole;
};

export type UserInfo = {
  betaFeatures: readonly string[];
  createdAt: string; // ISO8601
  email: string;
  emailVerified: boolean;
  firstName: string;
  freeSpins: number;
  hasAnnualSubscription: boolean;
  lastMonthlyRefreshAt: string; // ISO8601
  lastName: string;
  nextMonthlyRefreshEligibleAt: string; // ISO8601
  organizationID: string;
  planType: 'free' | 'pro' | 'unlimited';
  preferredName: string;
  premiumAccessUntil: string; // ISO8601
  purchasedSpins: number;
  spinsAvailable: number;
  stripeCustomerID: string;
  userID: string;
};

export type JobStatus =
  | 'FAILED'
  | 'QUEUED'
  | 'GENERATING_BVH'
  | 'GENERATING_PK'
  | 'COMPLETED';

export type UserChatRecord = {
  animationNumber?: number; // indicates *which* variation was selected at the time the user submitted the prompt
  jobID?: string; // indicates which jobID was selected at the time the user submitted the prompt
  prompt: string;
  type: 'USER';
};

export type SystemChatRecord = {
  animationNumbers?: number[][];
  jobIDs?: string[][];
  path: string;
  selectedAnimationNumber: number;
  selectedJobID: string;
  type: 'SYSTEM';
};

export type ChatRecord = UserChatRecord | SystemChatRecord;

export type JobFromAPI = {
  jobID: string;
  PK: string;
  SK: string;
  accessLevel: 'EDIT';
  accessorID: string;
  animationNumbers: number[];
  createdAt: string;
  createdBy: string;
  chatHistory: ChatRecord[];
  deleted: boolean;
  deletedAt: string;
  estimatedProcessingTime: number;
  estimatedQueueWaitTime: number;
  hideFromHistory: boolean;
  jobCreatedBy: string;
  lastGenerationRequestAt: string; // ISO8601 time of last submission
  numCurrentAnimations: number;
  numTotalAnimations: number;
  numFrames: number;
  prompt: string;
  sceneSettings?: SceneSettings; // These settings are ONLY ever available if you call GET /jobs/{jobID}
  sessionName: string;
  variationInfoByID: Record<string, Variation>;
};

export type Job = Omit<JobFromAPI, 'chatHistory'> & {
  chatHistory: [UserChatRecord, SystemChatRecord][];
};

export enum ProjectType {
  MOTION = 'MOTION',
  MIXED = 'MIXED',
  POSE = 'POSE',
}

export enum SceneType {
  MOTION = 'MOTION',
}

export enum GenerationType {
  MOTION_FROM_EXISTING = 'MOTION_FROM_EXISTING',
  MOTION_FROM_VIDEO = 'MOTION_FROM_VIDEO',
  MOTION_FROM_TEXT = 'MOTION_FROM_TEXT',
}

export type TimelineItemFromAPI = {
  createdBy: string;
  jobID: string;
  s3KeyBvh: string;
  s3KeyStitchBvh?: string;
  stitchEndDelay?: number;
  stitchStartDelay?: number;
  title: string;
  trimEnd?: number;
  trimStart?: number;
  variationNumber: number;
  bvhURL?: string;
  generationType: GenerationType;
  originalBvhURL: string;
  stitchBvhURL?: string;
  mp4URL?: string;
  comicMp4URL?: string;
};

export type GenerationHistoryFromAPI = Array<
  ExtendedGenerationRecord & {
    // These are referenced in code but not in the API response:
    renderedPngURL?: string;
    renderPrompt?: string;
  }
>;

export type ProjectFromAPI = {
  projectID: string;
  projectName: string;
  lastUpdatedAt: string;
  hideFromHistory: boolean;
  isBookmarked?: boolean;
  projectThumbnailURL?: string;

  createdAt: string;
  createdBy: string;
  pendingJobs: string[];
  sceneSettingsID: string;
  type: ProjectType;
  characters: Record<
    string,
    {
      characterID: string;
      instanceID: string;
      timeline: Array<TimelineItemFromAPI>;
      meshURL?: string;
      rotationOffsets?: RotationOffsets;
    }
  >;
  sceneSettings?: SceneSettings;

  // DynamoDB stuff:
  PK: string;
  SK: string;
  accessLevel: 'EDIT';
  accessorID: string;
};

export type Project = Omit<
  ProjectFromAPI,
  'PK' | 'SK' | 'accessLevel' | 'accessorID'
>;

export type SceneSettingsFromAPI = Partial<SceneSettings> & {
  backgroundSettings?: BackgroundSettingsV1;
  cameraSettings?: CameraSettingsV1;
  characterSettings?: CharacterSettingsV1;
  labelSettings?: LabelSettingsV1;
  playbackSettings?: PlaybackSettingsV1;
};

export type SceneTimelineKeyPoseFromAPI = {
  id?: string;
  frame: number;
  localJointRot: number[][];
  rootPosition: number[];
  smoothRoot2d?: number[];
};

export type SceneTimelineItemFromAPI = {
  appliedMotionEditJobID?: string;
  createdBy: string;
  generationType?: GenerationType | string;
  jobID: string;
  s3KeyOriginalBvh?: string;
  s3KeyBvh?: string;
  s3KeyStitchBvh?: string;
  staticHands?: string;
  stitchEndDelay?: number;
  stitchStartDelay?: number;
  title: string;
  trimEnd?: number;
  trimStart?: number;
  variationNumber: number;
  bvhURL?: string;
  originalBvhURL?: string;
  stitchBvhURL?: string;
  keyPoses?: SceneTimelineKeyPoseFromAPI[];
  mp4URL?: string;
  comicMp4URL?: string;
};

export type SceneTimelineUpdateItem = {
  appliedMotionEditJobID?: string;
  jobID: string;
  title?: string;
  trimEnd?: number;
  trimStart?: number;
  variationNumber?: number;
};

export type SceneObjectFromAPI = {
  objectID: string;
  referenceName: string;
  meshURL?: string;
  /** Presigned URL to identity.json when present (e.g. MHR blend-shape baseline). */
  identityJsonURL?: string;
  rotationOffsets?: RotationOffsets;
  thumbnailURL?: string;
  timeline: SceneTimelineItemFromAPI[];
};

export type SceneFromAPI = {
  sceneID: string;
  sceneName: string;
  sceneSettingsID?: string;
  sceneSettings?: SceneSettingsFromAPI;
  type: SceneType;
  objects: Record<string, SceneObjectFromAPI>;
  objectCount: Record<string, number>;
  pendingJobs: string[];
  createdAt: string;
  createdBy: string;
  /** Cartwheel user ID of the creator, when available. */
  createdByUser?: string;
  lastUpdatedAt?: string;
  sceneThumbnailURL?: string;
  thumbnailURL?: string;
};

export type Scene = SceneFromAPI;

export type ExpandedMediaRecordFromAPI = {
  createdAt: string;
  createdBy: string;
  duration: number; // in seconds for a video
  expandedUsedBy: {
    animationNumber: number;
    createdAt: string;
    jobID: string;
    mp4Path: string;
    sessionName: string;
  }[];
  fileExtension: string;
  lastUsedAt: string;
  mediaDownloadURL: string;
  mediaID: string;
  mediaName: string; // user editable string
  resolution: string; // "1920x1080"
  s3Key: string;
  thumbnailS3Key: string;
  thumbnailDownloadURL: string;
  usedBy: string[]; // a list of json data representing jobs that have used this media: '{animationNumber: number, createdAt: string, jobID: string}'.
  userID: string;
  PK: string;
  SK: string;
};

export type MediaRecordFromAPI = {
  createdAt: string;
  createdBy: string;
  duration: number; // in seconds for a video
  fileExtension: string;
  lastUsedAt: string;
  mediaDownloadURL: string;
  mediaID: string;
  mediaName: string; // user editable string
  resolution: string; // "1920x1080"
  s3Key: string;
  thumbnailS3Key: string;
  thumbnailDownloadURL: string;
  usedBy: string[]; // a list of json data representing jobs that have used this media: '{animationNumber: number, createdAt: string, jobID: string}'.
  userID: string;
  PK: string;
  SK: string;
};

export type MediaRecord = {
  createdAt: string;
  createdBy: string;
  duration: number; // in seconds for a video
  fileExtension: string;
  lastUsedAt: string;
  mediaDownloadURL: string;
  mediaID: string;
  mediaName: string; // user editable string
  resolution: string; // "1920x1080"
  s3Key: string;
  thumbnailS3Key: string;
  thumbnailDownloadURL: string;
  usedBy: { animationNumber: number; createdAt: string; jobID: string }[];
  userID: string;
  PK: string;
  SK: string;
};

export type JobExportRequestBody = {
  animationNumber: number;
  format: 'fbx' | 'glb' | 'usdz';
  includeMesh: boolean;
  wsConnectionID: string;
  wsHttpEndpoint: string;
};

export type Variation = {
  bvhURL: string;
  meshPath: string;
  mp4Path: string; // used in history
  comicMp4Path: string;
  comicMediaID?: string; // only present if the user has chosen to save their media during upload
  exitedQueueAt?: string;
  favorite: boolean;
  feedback: 'positive' | 'negative' | undefined;
  mayaDownloadUnlockedAt?: string;
  nonMayaDownloadUnlockedAt?: string;
  prompt: string;
  status: JobStatus;
  thumbnailPath: string;
  variationID: string;
  trimFrameStart?: number;
  trimFrameEnd?: number;
};

export type Feedback = {
  jobID: string;
  animationNumber: string;
  sentiment: Variation['feedback'];
  comment?: string;
  mp4Path: string;
  prompt: string;
};

export type BrowseFeedback = {
  jobID: string;
  sentiment:
    | 'EXCEPTIONAL'
    | 'NEUTRAL'
    | 'BAD_FEET'
    | 'BAD_MOTION'
    | 'POOR_LABELS';
  reason?: string;
};

export const frameRates = [24, 30, 60, 120];
export type FrameRate = (typeof frameRates)[number];

export type BackgroundSettingsV1 = {
  // canvasHeight and canvasWidth are only used for mp4 generation.
  canvasHeight?: number;
  canvasWidth?: number;
  gridCellSize?: number;
  hexColor: string; // #000000 - #FFFFFF
  useBackgroundGradient?: boolean;
  enableShadows?: boolean;
  showGrid?: boolean;
  showGroundPlane?: boolean;
  version: '1.0.0';
};
export type CameraSettingsV1 = {
  aspectRatio: number; // the value of the aspect ratio, so for 16:9 it's 16/9 (~1.777)
  serializedCamera: string; // JSON string
  cameraFollow: boolean;
  version: '1.0.0';
};

export type CharacterSettingsV1 = {
  charUploadID: string; // example: char-upload-DEFAULT-SMPLX-LIN
  rootPosition: [number, number, number]; // [0, 0, 0]
  rootRotation: [number, number, number]; // measured in radians
  skinHex: string; // example: #ffffff, 'default' is default
  faceExpression: string; // example: 1
  staticHands: string; // examples may be none, open, closed, pointing, etc.
  showSkeleton: boolean;
  enableRootMotion: boolean;
  version: '1.0.0';
};
export type LabelSettingsV1 = {
  size: number;
  style: string;
  text: string;
  color: string;
  alignment: 'left' | 'center' | 'right';
  layer: 'back' | 'front';
  marginRight: number;
  marginTop: number;
  marginLeft: number;
  version: '1.0.0';
};
export type PlaybackSettingsV1 = {
  frameRate: FrameRate;
  frameStepSize: number;
  playbackSpeed: number;
  version: '1.0.0';
};
export type SceneSettings = {
  backgroundSettings?: BackgroundSettingsV1;
  cameraSettings?: CameraSettingsV1;
  characterSettings?: CharacterSettingsV1;
  createdAt: string; // ISO8601
  createdBy: string; // userID
  labelSettings?: LabelSettingsV1;
  playbackSettings?: PlaybackSettingsV1;
  sceneID: string; // UUID generated during creation
  PK: string;
  SK: string;
};

export type AnnotationRecord = {
  annotationID: string;
  duration: number;
  finishedAt: string; // ISO8601
  interLabelQuality: number;
  isSkipped: boolean;
  longDescription: string;
  longEmbedding: number[];
  mediumDescription: string;
  mediumEmbedding: number[];
  motionData?: string; // JSON string containing all motion data (optional)
  reason: string;
  shortDescription: string;
  shortEmbedding: number[];
  startedAt: string; // ISO8601
  userID: string;
  videoID: string;
  PK: string;
  SK: string;
};

export type AnalyticsRecord = {
  interLabelQuality: number[];
  numJobsCompleted: number;
  numJobsSkipped: number;
  termsAcceptedAt: string; // ISO8601
  totalTimeWorked: number; // represented in hours
  tutorialCompleted: boolean;
  tutorialStep: number;
  userID: string;
  PK: string;
  SK: string;
};

export type StripeCardTypes =
  | 'amex'
  | 'diners'
  | 'discover'
  | 'eftpos_au'
  | 'jcb'
  | 'mastercard'
  | 'unionpay'
  | 'visa'
  | 'unknown';

export type StripePaymentCard = {
  brand: StripeCardTypes;
  checks: {
    address_line1_check: string | null;
    address_postal_code_check: string | null;
    cvc_check: 'pass' | 'fail' | 'unavailable' | 'unchecked';
  };
  country: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: 'credit';
  generated_from: string | null;
  last4: string;
  networks: {
    available: readonly StripeCardTypes[];
    preferred: string | null;
  };
  three_d_secure_usage: {
    supported: boolean;
  };
  wallet: string | null;
};

export type StripeAddress = {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | null;
  state: string | null;
};

export type StripePaymentMethod = {
  id: string;
  object: 'payment_method';
  billing_details: {
    address: StripeAddress;
    email: string | null;
    name: string | null;
    phone: string | null;
  };
  card: StripePaymentCard;
  created: 1679945299;
  customer: null;
  livemode: false;
  metadata: { [key: string]: string };
  type: 'card';
};

export type StripeCustomer = {
  id: string;
  object: 'customer';
  address: StripeAddress | null;
  balance: number;
  created: number;
  currency: string | null;
  default_source: string | null;
  delinquent: boolean;
  description: string | null;
  discount: string | null;
  email: string;
  invoice_prefix: string;
  invoice_settings: {
    custom_fields: { [key: string]: string } | null;
    default_payment_method: StripePaymentMethod | null;
    footer: string | null;
    rendering_options: string | null;
  };
  livemode: boolean;
  metadata: { [key: string]: string };
  name: string;
  next_invoice_sequence: number;
  phone: string | null;
  preferred_locales: string[];
  shipping: string | null;
  tax_exempt: 'exempt' | 'none' | 'reverse';
  test_clock: string | null;
  plans: StripePlan[];
};

export type StripeProduct = {
  id: string;
  object: 'product';
  active: boolean;
  attributes: string[];
  created: number;
  default_price: string;
  description: string;
  features: { [name: string]: string }[];
  images: string[];
  livemode: boolean;
  marketing_features: { name: string }[];
  metadata: { [key: string]: string };
  name: string;
  package_dimensions?: string | null;
  shippable?: boolean | null;
  statement_descriptor?: string | null;
  tax_code: string;
  type: string;
  unit_label?: string | null;
  updated: number;
  url?: string | null;
};

export type StripeInterval = 'day' | 'week' | 'month' | 'year';
export type StripeAggregateUsage =
  | 'last_during_period'
  | 'last_ever'
  | 'max'
  | 'sum';
export type StripeTierMode = 'graduated' | 'volume';
export type StripeUsageType = 'licensed' | 'metered';

export type StripePlan = {
  id: string;
  object: 'plan';
  active: boolean;
  aggregate_usage: StripeAggregateUsage | null;
  amount: number;
  amount_decimal: string;
  billing_scheme: 'per_unit' | 'tiered';
  created: number;
  currency: string;
  interval: StripeInterval;
  interval_count: number;
  livemode: boolean;
  metadata: {
    [key: string]: string;
  };
  nickname: string | null;
  product: string;
  tiers_mode: StripeTierMode | null;
  transform_usage: { divided_by: number; round: 'up' | 'down' } | null;
  trial_period_days: number | null;
  usage_type: StripeUsageType;
};

export type StripePrice = {
  id: string;
  object: 'price';
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount?: number | null;
  livemode: boolean;
  lookup_key?: string | null;
  metadata: { [key: string]: string };
  nickname?: string | null;
  product: string;
  recurring: {
    aggregate_usage?: StripeAggregateUsage | null;
    interval: StripeInterval;
    interval_count: number;
    meter?: string | null;
    trial_period_days?: number | null;
    usage_type: StripeUsageType;
  };
  tax_behavior: string;
  tiers_mode?: StripeTierMode | null;
  transform_quantity?: string | null;
  type: 'recurring' | 'one_time';
  unit_amount: number;
  unit_amount_decimal: string;
};

export type StripePortalSession = {
  id: string;
  object: 'billing_portal.session';
  configuration: string;
  created: number;
  customer: string;
  flow: string | null;
  livemode: boolean;
  locale: string | null;
  on_behalf_of: string | null;
  return_url: string;
  url: string;
};

export type SpinTransaction = {
  amount: number; // Positive for credits earned, negative for credits spent
  createdAt: string;
  description: string; // Human readable description of the transaction
  jobID?: string; // Optional reference to the job if the transaction is related to a specific job
  transactionType: string; // e.g., 'FOLLOW_UP_PROMPT', 'BROWSE', 'SUBSCRIPTION', 'MONTHLY_REFRESH', 'REFUND'
  userID: string;
  PK: string;
  SK: string;
};

export enum HAND_OPTIONS {
  DEFAULT = 'default',
  CLAW = 'claw', // from claw
  CURLED = 'curled', // from relaxCurled
  FIST = 'fist', // from fist
  FIST_CLENCHED = 'fist_clenched', // from fistClenched
  FIST_RELAXED = 'fist_relaxed', // from fistRelaxed
  GRIPPING = 'gripping', // from grab
  LOVE_YOU = 'love_you', // from loveYou
  OPEN_LOOSE = 'open_loose', // from open
  OPEN_TIGHT = 'open_tight', // from tightOpen
  PEACE = 'peace', // from peace
  POINTING = 'pointing', // from point
  POINTING_COOL = 'pointing_cool', // from pointCool
  RELAXED = 'relaxed', // from relax
  ROCK = 'rock', // from rock
  SPLAY = 'splay', // from splay
  THUMBS_UP = 'thumbs_up', // from thumbsUp
}

export enum FACE_EXPRESSIONS {
  DEFAULT = 'Default',
  NEUTRAL = 'Neutral',
  ANGRY = 'Angry',
  ANNOYED = 'Annoyed',
  BORED = 'Bored',
  CONFUSION = 'Confusion',
  DELIGHTED_GRIN = 'DelightedGrin',
  DELIGHTED = 'Delighted',
  EXCITED = 'Excited',
  GOOF = 'Goof',
  HAPPY_LOOK_LEFT = 'HappyLookLeft',
  HAPPY_LOOK_RIGHT = 'HappyLookRight',
  LOVE = 'Love',
  NERVOUS = 'Nervous',
  PAIN = 'Pain',
  PLEASED = 'Pleased',
  PUZZLED = 'Puzzled',
  RAGE = 'Rage',
  ROLL_LOOK_LEFT = 'RollLookLeft',
  ROLL_LOOK_RIGHT = 'RollLookRight',
  SAD = 'Sad',
  SAD_EYES_CLOSED = 'SadEyesClosed',
  SAD_LOOK_LEFT = 'SadLookLeft',
  SAD_LOOK_RIGHT = 'SadLookRight',
  SQUEEZE = 'Squeeze',
  STRETCH = 'Stretch',
  STUNNED = 'Stunned',
  SURPRISE = 'Surprise',
  SUSPICIOUS = 'Suspicious',
  THINKING_LOOK_LEFT = 'ThinkingLookLeft',
  THINKING_LOOK_RIGHT = 'ThinkingLookRight',
  WINK = 'Wink',
}

export interface DownloadHistoryRecord {
  readonly animationNumber: number;
  readonly characterName: string;
  readonly dateRequested: string;
  readonly downloadPath: string;
  readonly exportType: string;
  readonly jobID: string;
  readonly sessionName: string;
  readonly mp4Path: string;
}

export interface ProjectDownloadRecord {
  readonly characterName: string;
  readonly dateRequested: string;
  readonly downloadPath: string;
  readonly exportType: string;
  readonly mp4Path: string;
  readonly projectID: string;
  readonly projectName: string;
  readonly projectType: ProjectType;
}

export type RotationOffsets = {
  head: [number, number, number];
  left_ankle: [number, number, number];
  right_ankle: [number, number, number];
  left_collar: [number, number, number];
  right_collar: [number, number, number];
  left_hip: [number, number, number];
  right_hip: [number, number, number];
  left_shoulder: [number, number, number];
  right_shoulder: [number, number, number];
  neck: [number, number, number];
};

type BonesMap = Record<string, string>;
export type EnhancedGLTF = GLTF & ObjectMap & { bonesMap: BonesMap };

// #endregion
